import React, { useEffect } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArticleIcon from "@mui/icons-material/Article";
import CommentIcon from "@mui/icons-material/Comment";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import VisibilityIcon from "@mui/icons-material/Visibility";
import dayjs from "dayjs";
import {
  AreaChart, Area, PieChart, Pie, Cell, Tooltip as RechartTooltip,
  XAxis, YAxis, ResponsiveContainer, Legend,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AnalyticsCard } from "../components/AnalyticsCard";
import { BlogBreadcrumbs } from "../components/BlogBreadcrumbs";
import { PostStatusChip } from "../components/PostStatusChip";
import { fetchOverview } from "../analyticsSlice";
import { fetchPosts, updatePostStatus } from "../blogSlice";
import { formatCount } from "../../../utils/formatCount";

const STATUS_COLORS = {
  draft:     "#94a3b8",
  published: "#065F46",
  scheduled: "#f59e0b",
  archived:  "#EA001B",
};

export default function BlogDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { overview, loading: aLoading, error: aError } = useSelector((state) => state.analytics);
  const { posts, loading: pLoading } = useSelector((state) => state.blog);

  useEffect(() => {
    dispatch(fetchOverview());
    dispatch(fetchPosts({ limit: 5, sort: "-createdAt" }));
  }, [dispatch]);

  const statusGroups = posts.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});
  const pieData = Object.entries(statusGroups).map(([name, value]) => ({ name, value }));
  const featuredCount = posts.filter((p) => p.isFeatured).length;

  return (
    <Box sx={{ p: 3 }}>
      <BlogBreadcrumbs items={[{ label: "Blog Dashboard" }]} />
      <Typography variant="h5" sx={{ fontWeight: 700, color: "#362B24", fontFamily: "Heebo", mb: 2 }}>
        Blog Dashboard
      </Typography>

      {(aLoading || pLoading) && <CircularProgress size={24} sx={{ color: "#065F46", mb: 2 }} />}
      {aError && <Alert severity="error" sx={{ mb: 2 }}>{aError}</Alert>}

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <AnalyticsCard title="Total Posts"    value={overview?.totalPosts    ?? posts.length} icon={ArticleIcon} />
        </Grid>
        <Grid item xs={6} md={3}>
          <AnalyticsCard title="Total Views"    value={overview?.totalViews    ?? "—"}          icon={VisibilityIcon} />
        </Grid>
        <Grid item xs={6} md={3}>
          <AnalyticsCard title="Total Comments" value={overview?.totalComments ?? "—"}          icon={CommentIcon} />
        </Grid>
        <Grid item xs={6} md={3}>
          <AnalyticsCard title="Featured Posts" value={featuredCount}                           icon={StarIcon} />
        </Grid>
      </Grid>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, borderRadius: "24px", boxShadow: "3px 2px 20px 0px rgba(0,0,0,0.04)" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#362B24", fontFamily: "Heebo", mb: 1 }}>
              Daily Views
            </Typography>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={overview?.dailyViews || []}>
                <defs>
                  <linearGradient id="dashGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#065F46" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#065F46" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tickFormatter={(d) => dayjs(d).format("MMM D")} tick={{ fontSize: 11, fill: "#362B24" }} />
                <YAxis tick={{ fontSize: 11, fill: "#362B24" }} />
                <RechartTooltip labelFormatter={(d) => dayjs(d).format("MMM D, YYYY")} />
                <Area type="monotone" dataKey="views" stroke="#065F46" fill="url(#dashGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: "24px", boxShadow: "3px 2px 20px 0px rgba(0,0,0,0.04)" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#362B24", fontFamily: "Heebo", mb: 1 }}>
              Posts by Status
            </Typography>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || "#94a3b8"} />
                  ))}
                </Pie>
                <RechartTooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#362B24", fontFamily: "Heebo", mb: 1 }}>
        Recent Posts
      </Typography>
      <Paper sx={{ borderRadius: "24px", boxShadow: "3px 2px 20px 0px rgba(0,0,0,0.04)", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f9fafb" }}>
                <TableCell sx={{ fontFamily: "Heebo", fontWeight: 600, color: "#362B24" }}>Title</TableCell>
                <TableCell sx={{ fontFamily: "Heebo", fontWeight: 600, color: "#362B24" }}>Status</TableCell>
                <TableCell sx={{ fontFamily: "Heebo", fontWeight: 600, color: "#362B24" }}>Views</TableCell>
                <TableCell sx={{ fontFamily: "Heebo", fontWeight: 600, color: "#362B24" }}>Published At</TableCell>
                <TableCell align="right" sx={{ fontFamily: "Heebo", fontWeight: 600, color: "#362B24" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.slice(0, 5).map((post) => (
                <TableRow key={post._id} hover>
                  <TableCell sx={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "Heebo", color: "#362B24" }}>
                    {post.title}
                  </TableCell>
                  <TableCell><PostStatusChip status={post.status} /></TableCell>
                  <TableCell sx={{ fontFamily: "Heebo", color: "#362B24" }}>{formatCount(post.views)}</TableCell>
                  <TableCell sx={{ fontFamily: "Heebo", color: "#362B24" }}>
                    {post.publishedAt ? dayjs(post.publishedAt).format("MMM D, YYYY") : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => navigate(`/blog/posts/${post._id}/edit`)}>
                        <EditIcon fontSize="small" sx={{ color: "#065F46" }} />
                      </IconButton>
                    </Tooltip>
                    {post.status !== "published" && (
                      <Tooltip title="Publish">
                        <IconButton size="small" onClick={() => dispatch(updatePostStatus({ id: post._id, status: "published" }))}>
                          <VisibilityIcon fontSize="small" sx={{ color: "#065F46" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
